import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { QrCode, Camera, X, Check, RefreshCw } from 'lucide-react';

const BLUE = '#0088CC';
const BLUE_LIGHT = '#00CCFF';

const PROMO_META = {
  first_timer: { icon: '🆕', label: 'First Timer' },
  group_of_4: { icon: '👥', label: 'Group of 4' },
  birthday: { icon: '🎂', label: 'Birthday' },
};

const authAxios = () => {
  const token = localStorage.getItem('bar_owner_token');
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

export default function OwnerPromoRedemptions() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [verified, setVerified] = useState(null); // {valid, promo_type, title, patron_name}
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        authAxios().get('/owner/promo-redemptions/stats'),
        authAxios().get('/owner/promo-redemptions/recent'),
      ]);
      setStats(s.data);
      setRecent(r.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const parseToken = (raw) => {
    // Accept JSON payload or a bare token string
    if (!raw) return null;
    const trimmed = raw.trim();
    try {
      const obj = JSON.parse(trimmed);
      return obj.token || null;
    } catch {
      return trimmed;
    }
  };

  const verify = async (raw) => {
    const token = parseToken(raw);
    if (!token) return toast.error('No token');
    setBusy(true);
    try {
      const res = await axios.get(`${API}/promo/verify/${token}`);
      setVerified({ ...res.data, token });
      if (!res.data.valid) {
        toast.error(res.data.already_redeemed ? 'Already redeemed' : 'Invalid token');
      }
    } catch (err) {
      toast.error('Verify failed');
    } finally { setBusy(false); }
  };

  const redeem = async () => {
    if (!verified?.token) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/promo/redeem`, { token: verified.token });
      if (res.data.success) {
        toast.success(`✅ Redeemed: ${res.data.title} for @${res.data.patron_name}`);
        setVerified({ ...verified, already_redeemed: true, valid: false });
        setManualToken('');
        load();
      } else {
        toast.error(res.data.error || 'Failed');
      }
    } catch (err) {
      toast.error('Redeem failed');
    } finally { setBusy(false); }
  };

  const startScanner = async () => {
    setScannerOpen(true);
    setVerified(null);
    // Lazy-load the html5-qrcode library
    try {
      const mod = await import('html5-qrcode');
      const Html5Qrcode = mod.Html5Qrcode;
      // wait a beat for the DOM node to mount
      setTimeout(async () => {
        try {
          html5QrCodeRef.current = new Html5Qrcode('promo-qr-reader');
          await html5QrCodeRef.current.start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: 250 },
            async (decoded) => {
              await stopScanner();
              await verify(decoded);
            },
            () => { /* ignore per-frame errors */ }
          );
        } catch (err) {
          toast.error('Camera access denied — use manual entry');
          setScannerOpen(false);
        }
      }, 100);
    } catch { toast.error('QR scanner unavailable'); }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch { /* ignore */ }
      html5QrCodeRef.current = null;
    }
    setScannerOpen(false);
  };

  const StatCard = ({ label, today, week, icon }) => (
    <Card
      className="p-3"
      style={{ background: 'rgba(10, 20, 35, 0.8)', border: `1px solid ${BLUE}44` }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">{icon}</span>
        <span className="text-xs uppercase tracking-widest" style={{ color: BLUE_LIGHT }}>{label}</span>
      </div>
      <div className="flex justify-between text-xs mt-1">
        <span className="text-slate-400">Today</span>
        <span className="font-mono font-bold" style={{ color: BLUE_LIGHT }}>{today}</span>
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-slate-400">This Week</span>
        <span className="font-mono font-bold" style={{ color: BLUE_LIGHT }}>{week}</span>
      </div>
    </Card>
  );

  return (
    <Card
      data-testid="owner-promo-redemptions"
      className="p-5 mt-4"
      style={{
        background: 'rgba(10, 20, 35, 0.7)',
        border: `1px solid ${BLUE}66`,
        boxShadow: `0 0 20px ${BLUE}22`
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2
          className="text-xl font-bold"
          style={{ color: BLUE_LIGHT, textShadow: `0 0 10px ${BLUE}` }}
        >
          📊 Promo Redemptions
        </h2>
        <div className="flex gap-2">
          <Button
            data-testid="refresh-redemptions-btn"
            onClick={load}
            size="sm"
            variant="outline"
            className="border-slate-700"
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button
            data-testid="scan-promo-btn"
            onClick={startScanner}
            size="sm"
            className="font-bold"
            style={{ background: BLUE_LIGHT, color: '#001525' }}
          >
            <QrCode className="w-4 h-4 mr-2" /> Scan Promo QR
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard label="First Timer" icon="🆕" today={stats.first_timer.today} week={stats.first_timer.week} />
          <StatCard label="Group of 4" icon="👥" today={stats.group_of_4.today} week={stats.group_of_4.week} />
          <StatCard label="Birthday" icon="🎂" today={stats.birthday.today} week={stats.birthday.week} />
          <StatCard label="Total" icon="🎯" today={stats.total.today} week={stats.total.week} />
        </div>
      )}

      {/* Manual token entry */}
      <div className="p-3 rounded-lg mb-4"
        style={{ background: 'rgba(0,0,0,0.4)', border: `1px dashed ${BLUE}66` }}
      >
        <p className="text-xs mb-2" style={{ color: BLUE_LIGHT }}>
          Or manually enter/paste the token from a patron&apos;s QR:
        </p>
        <div className="flex gap-2">
          <Input
            data-testid="manual-token-input"
            placeholder="Paste QR data or token hex..."
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            className="flex-1 text-xs"
            style={{ background: 'rgba(0,0,0,0.6)', border: `1px solid ${BLUE}44`, color: 'white' }}
          />
          <Button
            data-testid="verify-token-btn"
            onClick={() => verify(manualToken)}
            disabled={!manualToken || busy}
            style={{ background: BLUE, color: '#fff' }}
          >
            Verify
          </Button>
        </div>
      </div>

      {/* Recent redemptions */}
      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: BLUE_LIGHT }}>
        Recent Redemptions
      </p>
      {recent.length === 0 ? (
        <p className="text-xs text-slate-500">No redemptions yet.</p>
      ) : (
        <div className="space-y-1">
          {recent.map(r => (
            <div key={r.id} data-testid={`recent-${r.id}`}
              className="flex justify-between items-center text-xs p-2 rounded"
              style={{ background: 'rgba(0,0,0,0.4)' }}
            >
              <span>
                {PROMO_META[r.promo_type]?.icon || '🎁'} <b>{r.title}</b>
                <span className="text-slate-400 ml-2">@{r.patron_name}</span>
              </span>
              <span className="text-slate-500 text-[10px]">
                {new Date(r.redeemed_at).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Scanner modal */}
      {scannerOpen && (
        <div
          data-testid="scanner-modal"
          className="fixed inset-0 z-[10000] bg-black/95 flex flex-col items-center justify-center p-4"
        >
          <button
            onClick={stopScanner}
            className="absolute top-4 right-4 p-2 rounded-full"
            style={{ background: 'rgba(255,255,255,0.1)' }}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <p className="text-white mb-3 flex items-center gap-2">
            <Camera className="w-4 h-4" /> Point camera at patron&apos;s QR code
          </p>
          <div id="promo-qr-reader" ref={scannerRef}
            style={{ width: 300, height: 300, background: '#000',
                     border: `2px solid ${BLUE_LIGHT}`,
                     boxShadow: `0 0 20px ${BLUE_LIGHT}88` }}
          />
        </div>
      )}

      {/* Verify + Redeem modal */}
      {verified && (
        <div
          data-testid="verify-modal"
          className="fixed inset-0 z-[10001] bg-black/90 flex items-center justify-center p-6"
        >
          <Card
            className="p-6 max-w-sm w-full"
            style={{
              background: '#0d1117',
              border: `2px solid ${verified.valid ? BLUE_LIGHT : '#FF4444'}`,
              boxShadow: `0 0 30px ${verified.valid ? BLUE_LIGHT : '#FF4444'}66`
            }}
          >
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">{verified.icon || '🎁'}</div>
              <h3 className="font-bold text-xl text-white">{verified.title}</h3>
              {verified.patron_name && (
                <p className="text-sm text-slate-400 mt-1">@{verified.patron_name}</p>
              )}
              {verified.description && (
                <p className="text-xs text-slate-300 mt-2">{verified.description}</p>
              )}
              {verified.already_redeemed && (
                <p className="text-red-400 mt-3 font-bold text-sm">⛔ Already redeemed</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => { setVerified(null); setManualToken(''); }}
                variant="outline"
                className="flex-1 border-slate-600"
              >
                Cancel
              </Button>
              {verified.valid && (
                <Button
                  data-testid="confirm-redeem-btn"
                  onClick={redeem}
                  disabled={busy}
                  className="flex-1 font-bold"
                  style={{ background: BLUE_LIGHT, color: '#001525' }}
                >
                  <Check className="w-4 h-4 mr-2" /> REDEEM
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
}
